import React, { useState, useRef } from 'react'
import { t, jt } from 'ttag'
import { Box, CircularProgress, Typography } from '@material-ui/core'
import YumDialog from '../../../../components/YumDialog/YumDialog'
import YumDialogTitle from '../../../../components/YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../../../../components/YumDialogContent/YumDialogContent'
import YumDialogContentText from '../../../../components/YumDialogContentText/YumDialogContentText'
import YumDialogActions from '../../../../components/YumDialogActions/YumDialogActions'
import YumCancelButton from '../../../../components/YumCancelButton/YumCancelButton'
import YumSubmitButton from '../../../../components/YumSubmitButton/YumSubmitButton'
import {
  CompanyId,
  DeleteTeams$Request,
  DivisionCategoryId,
  DivisionId,
  DivisionRow,
} from '../../../../services/api.types'
import { formatThousands } from '../../../../common/helpers/translations'
import { red } from '../../../../styles/material-ui-theme-v2'

interface DeleteTeamsProps {
  open: boolean
  onClose: () => void
  onSubmit: (payload: DeleteTeams$Request & { companyId?: CompanyId; categoryId?: DivisionCategoryId }) => void
  companyId: CompanyId
  teamsToDelete: DivisionId[]
  companyTeams: DivisionRow[]
  loading: boolean
  categoryId?: DivisionCategoryId
}

const DeleteTeams: React.FC<DeleteTeamsProps> = ({
  open,
  onClose,
  onSubmit,
  companyId,
  teamsToDelete,
  companyTeams,
  loading,
  categoryId,
}) => {
  const isSubmittingRef = useRef(false)

  // Close dialog when request completes
  React.useEffect(() => {
    // If we were loading and now we're not, and we had submitted the form, close the dialog
    if (!loading && isSubmittingRef.current) {
      isSubmittingRef.current = false
      onClose()
    }
  }, [loading, onClose])

  // Calculate affected users count
  const getAffectedUsersCount = () => {
    //console.log('companyTeams:', companyTeams)
    return companyTeams.filter((team) => teamsToDelete.includes(team.id)).reduce((sum, team) => sum + team.members, 0)
  }

  const affectedUsers = getAffectedUsersCount()

  // Handle submit
  const handleSubmit = () => {
    // Mark that we've submitted the form
    isSubmittingRef.current = true

    onSubmit({
      companyId,
      divisionIds: teamsToDelete,
      categoryId,
    })
  }

  return (
    <YumDialog open={open}>
      <YumDialogTitle handleClose={onClose}>
        {categoryId ? t`Confirm group deletion` : t`Confirm team deletion`}
      </YumDialogTitle>
      <YumDialogContent>
        <YumDialogContentText>
          {teamsToDelete.length === 1 ? (
            <>
              {categoryId
                ? jt`Are you sure you want to delete ${(<strong key="count">1</strong>)} group?`
                : jt`Are you sure you want to delete ${(<strong key="count">1</strong>)} team?`}
            </>
          ) : (
            <>
              {categoryId
                ? jt`Are you sure you want to delete ${(<strong key="count">{teamsToDelete.length}</strong>)} groups?`
                : jt`Are you sure you want to delete ${(<strong key="count">{teamsToDelete.length}</strong>)} teams?`}
            </>
          )}
        </YumDialogContentText>
        {affectedUsers > 0 && (
          <YumDialogContentText style={{ color: red, marginTop: '10px' }}>
            {affectedUsers === 1 ? (
              <>
                {categoryId
                  ? jt`Warning: ${(<strong>1</strong>)} user will lose their group assignment.`
                  : jt`Warning: ${(<strong>1</strong>)} user will lose their team assignment.`}
              </>
            ) : (
              <>
                {categoryId
                  ? jt`Warning: ${(<strong>{formatThousands(affectedUsers)}</strong>)} users will lose their group assignments.`
                  : jt`Warning: ${(<strong>{formatThousands(affectedUsers)}</strong>)} users will lose their team assignments.`}
              </>
            )}
          </YumDialogContentText>
        )}
      </YumDialogContent>
      <YumDialogActions>
        <YumCancelButton onClick={onClose} disabled={loading} />
        <YumSubmitButton
          text={t`Delete`}
          onClick={handleSubmit}
          disabled={loading}
          submitInProgress={loading}
          immersiveLoader={true}
          color="error"
        />
      </YumDialogActions>
    </YumDialog>
  )
}

export default DeleteTeams
